import { useParams, useNavigate } from 'react-router-dom';
import { useOffers } from './store/useOffers';
import { useToast } from './store/useToast';
import { useLocations } from './store/useLocations';
import { useTiers } from './store/useTiers';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { useOfferManagement } from '../../redux/offers/offerHooks';
import { Button, Card, Tabs, Badge, ConfirmDialog, Toggle } from './components/ui';
import { Modal } from './components/ui/Modal';
import StatusBadge from './components/data/StatusBadge';
import PageHeader from './components/layout/PageHeader';
import Breadcrumb from './components/layout/Breadcrumb';
import { TextField, NumberField, TextArea, Select } from './components/forms';
import { IconTag, IconActivity, IconDollar } from './components/ui/Icons';
import { formatDate, formatCurrency, formatNumber, formatDateShort } from './utils/format';
import KPICard from './components/data/KPICard';
import KPIRow from './components/data/KPIRow';
import { LineChartComponent } from './components/charts';
import { BarChartComponent } from './components/charts';
import { offerMetrics } from './data/mockOfferAnalytics';
import { useState, useEffect, useMemo } from 'react';
import DesktopLayout from '../layout/DesktopLayout';
import { EmptyState } from './components/ui';
import { getOfferMenuItems } from './data/mockMenuItems';
import type { MenuItem, Offer, OfferType, OfferStatus, PromoCode, OfferLocationOverride } from './data/types';
import './OfferDetail.css';

const typeLabels: Record<OfferType, string> = {
  'percentage-off': 'Percentage Off', 'flat-dollar-off': 'Flat $ Off', 'bogo': 'BOGO',
  'free-item': 'Free Item', 'free-delivery': 'Free Delivery', 'combo-bundle': 'Combo / Bundle',
  'happy-hour': 'Happy Hour', 'first-time': 'First-Time Customer', 'loyalty-linked': 'Loyalty-Linked',
};

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="offer-detail-info-row">
      <span className="offer-detail-info-label">{label}</span>
      <span className="offer-detail-info-value">{value}</span>
    </div>
  );
}

// ── Promo Code Form Modal ──
function PromoCodeFormModal({
  open,
  onClose,
  onSave,
  initial,
  existingCodes,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: Omit<PromoCode, 'id' | 'offerId' | 'usageCount' | 'createdAt'>) => void;
  initial?: PromoCode;
  existingCodes: PromoCode[];
}) {
  const isEditing = !!initial;
  const [code, setCode] = useState(initial?.code || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [effectiveFrom, setEffectiveFrom] = useState(initial?.effectiveFrom || '');
  const [effectiveUntil, setEffectiveUntil] = useState(initial?.effectiveUntil || '');
  const [singleUse, setSingleUse] = useState(initial?.singleUse || false);
  const [maxRedemptions, setMaxRedemptions] = useState<number | ''>(initial?.maxRedemptions ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) e.code = 'Promo code is required.';
    else if (trimmed.length < 3) e.code = 'Code must be at least 3 characters.';
    else if (trimmed.length > 30) e.code = 'Code cannot exceed 30 characters.';
    else if (!/^[A-Z0-9-]+$/.test(trimmed)) e.code = 'Only letters, numbers, and hyphens allowed.';
    else if (!isEditing) {
      const dup = existingCodes.find((c) => c.code.toUpperCase() === trimmed);
      if (dup) e.code = `Code "${trimmed}" already exists on this offer.`;
    }
    if (description.length > 120) e.description = 'Description cannot exceed 120 characters.';
    if (effectiveFrom && effectiveUntil && effectiveUntil < effectiveFrom) {
      e.effectiveUntil = 'End date must be after start date.';
    }
    if (maxRedemptions !== '' && Number(maxRedemptions) < 1) e.maxRedemptions = 'Must be at least 1.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      code: code.trim().toUpperCase(),
      description: description.trim(),
      effectiveFrom: effectiveFrom || null,
      effectiveUntil: effectiveUntil || null,
      singleUse,
      maxRedemptions: maxRedemptions === '' ? null : Number(maxRedemptions),
      status: initial?.status || 'active',
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Promo Code' : 'Add Promo Code'}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>{isEditing ? 'Save Changes' : 'Add Code'}</Button>
        </>
      }
    >
      <div className="offer-detail-modal-form">
        <TextField
          label="Promo Code"
          value={code}
          onChange={(v) => setCode(v.toUpperCase())}
          maxLength={30}
          required
          error={errors.code}
          disabled={isEditing}
          helpText={isEditing ? 'Code cannot be changed after creation.' : '3–30 characters: letters, numbers, hyphens. Auto-uppercased.'}
        />
        <div className="offer-detail-char-count">{code.length}/30</div>
        <TextArea
          label="Description"
          value={description}
          onChange={setDescription}
          maxLength={120}
          helpText="Optional — visible to BO users only."
          error={errors.description}
        />
        <div className="offer-detail-row-gap-12">
          <TextField
            label="Effective From"
            type="date"
            value={effectiveFrom}
            onChange={setEffectiveFrom}
          />
          <TextField
            label="Effective Until"
            type="date"
            value={effectiveUntil}
            onChange={setEffectiveUntil}
            error={errors.effectiveUntil}
          />
        </div>
        <div className="offer-detail-row-between">
          <span className="offer-detail-text-13">Single-use (one redemption per customer)</span>
          <Toggle checked={singleUse} onChange={setSingleUse} />
        </div>
        <NumberField
          label="Max Redemptions"
          value={maxRedemptions}
          onChange={(v) => setMaxRedemptions(v as number | '')}
          min={1}
          helpText="Leave empty for unlimited."
          error={errors.maxRedemptions}
        />
      </div>
    </Modal>
  );
}

// ── Location Override Form Modal ──
function LocationOverrideFormModal({
  open,
  onClose,
  onSave,
  initial,
  availableLocations,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (override: OfferLocationOverride) => void;
  initial?: OfferLocationOverride;
  availableLocations: { id: string; name: string }[];
}) {
  const isEditing = !!initial;
  const [locationId, setLocationId] = useState(initial?.locationId || '');
  const [discountValue, setDiscountValue] = useState<number | ''>(initial?.discountValue ?? '');
  const [status, setStatus] = useState<string>(initial?.status || '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!locationId) e.locationId = 'Select a location.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      locationId,
      discountValue: discountValue === '' ? undefined : Number(discountValue),
      status: status ? (status as OfferStatus) : undefined,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Location Override' : 'Add Location Override'}
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>{isEditing ? 'Save' : 'Add Override'}</Button>
        </>
      }
    >
      <div className="offer-detail-modal-form">
        <Select
          label="Location"
          value={locationId}
          onChange={setLocationId}
          options={[{ value: '', label: 'Select location...' }, ...availableLocations.map((l) => ({ value: l.id, label: l.name }))]}
          required
          disabled={isEditing}
          error={errors.locationId}
        />
        <NumberField
          label="Override Discount Value"
          value={discountValue}
          onChange={(v) => setDiscountValue(v as number | '')}
          suffix="%"
          min={0.01}
          max={100}
          helpText="Leave empty to use brand default."
        />
        <Select
          label="Override Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: 'Use brand default' },
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
          ]}
        />
      </div>
    </Modal>
  );
}

export default function OfferDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { selectedBranch } = useBranch();
  const {
    offers: localOffers, transitionStatus, deleteOffer, archiveOffer, updateOffer,
    addPromoCode, updatePromoCode, deactivatePromoCode,
    addLocationOverride, updateLocationOverride, removeLocationOverride,
  } = useOffers();
    const {
      fetchOffers,
      offers: { offers: managedOffers },
      toggleOfferStatusOnServer,
    } = useOfferManagement();
  const { addToast } = useToast();
  const { locations } = useLocations();
  const { tiers } = useTiers();

  const offer = id
    ? managedOffers.find((o) => o.id === id) ?? localOffers.find((o) => o.id === id)
    : undefined;
    console.log({offer});
    

  // Map raw orderType UUIDs (as stored on the server) to merchant-configured
  // display names, e.g. "Dine-In" or "Online Pickup". Falls back to static
  // slug labels when an entry isn't present on the branch.
  const orderTypeLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    const raw = (selectedBranch as Record<string, any> | null | undefined)?.orderTypes;
    if (Array.isArray(raw)) {
      raw.forEach((entry: { id?: string; typeName?: string; isEnabled?: number | boolean }) => {
        const otId = String(entry?.id ?? '').trim();
        const name = String(entry?.typeName ?? '').trim();
        if (otId && name) map[otId] = name;
      });
    }
    return map;
  }, [selectedBranch]);
  const [activeTab, setActiveTab] = useState('overview');
  const [confirmAction, setConfirmAction] = useState<{ type: string; status?: OfferStatus; codeId?: string; locationId?: string } | null>(null);

  useEffect(() => {
    if (!id || offer) return;
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId) return;
    void fetchOffers(locationId, token, { force: true }).catch(() => {/* fallback to current FE state */});
  }, [id, offer, selectedBranch?.id, selectedBranch?.locationId, auth?.accessToken, auth?.access_token, fetchOffers]);

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuItemsLoading, setMenuItemsLoading] = useState(true);
  useEffect(() => {
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId) return;
    void getOfferMenuItems(locationId, token)
      .then((items) => { setMenuItems(items); setMenuItemsLoading(false); })
      .catch(() => { setMenuItems([]); setMenuItemsLoading(false); });
  }, [selectedBranch?.id, selectedBranch?.locationId, auth?.accessToken, auth?.access_token]);

  // Promo code form state
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | undefined>(undefined);

  // Location override form state
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [editingOverride, setEditingOverride] = useState<OfferLocationOverride | undefined>(undefined);

  if (!offer) {
    return (
      <DesktopLayout>
        <div className="offer-detail-page">
          <EmptyState
            icon={<IconTag size={36} />}
            title="Loading offer..."
            description="Fetching latest offers list."
            action={<Button variant="primary" onClick={() => navigate('/offers')}>Back to Offers</Button>}
          />
        </div>
      </DesktopLayout>
    );
  }

  const handleTransition = async (status: OfferStatus) => {
    if (status === 'active' && offer.type === 'loyalty-linked' && offer.typeConfig?.kind === 'loyalty-linked') {
      const tierId = offer.typeConfig.tierId;
      if (tierId && !tiers.find((t) => t.id === tierId)) {
        addToast('Cannot activate: linked loyalty tier no longer exists.', 'error');
        return;
      }
    }
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    const isManaged = managedOffers.some((o) => o.id === offer.id);
    const canPatch =
      isManaged &&
      Boolean(locationId) &&
      Boolean(token) &&
      (status === 'active' || status === 'paused' || status === 'draft');

    if (canPatch) {
      try {
        await toggleOfferStatusOnServer(offer.id, locationId, status, token);
        transitionStatus(offer.id, status);
        await fetchOffers(locationId, token, { force: true });
      } catch (e) {
        addToast(e instanceof Error ? e.message : 'Failed to update offer status', 'error');
        return;
      }
    } else {
      transitionStatus(offer.id, status);
    }
    addToast(`Offer ${status === 'active' ? 'activated' : status === 'paused' ? 'paused' : status === 'archived' ? 'archived' : 'updated'}`, 'success');
  };

  const handleDuplicate = () => {
    navigate(`/offers/duplicate/${encodeURIComponent(offer.id)}`);
  };

  const handleDelete = () => {
    if (offer.redemptionCount > 0) {
      archiveOffer(offer.id);
      addToast('Offer archived (has redemption history)', 'success');
    } else {
      deleteOffer(offer.id);
      addToast('Offer deleted');
    }
    navigate('/offers');
  };

  const getItemName = (itemId: string) => menuItemsLoading ? '…' : (menuItems.find((m) => m.id === itemId)?.name ?? itemId);
  const getTierName = (tierId: string) => tiers.find((t) => t.id === tierId)?.name || tierId;
  const getLocationName = (locId: string) => locations.find((l) => l.id === locId)?.name || locId;

  // Available locations for overrides (exclude already overridden ones)
  const overriddenLocationIds = offer.locationOverrides.map((o) => o.locationId);
  const availableLocationsForOverride = locations.filter((l) => !overriddenLocationIds.includes(l.id));

  // Promo code handlers
  const handleAddPromoCode = (data: Omit<PromoCode, 'id' | 'offerId' | 'usageCount' | 'createdAt'>) => {
    addPromoCode(offer.id, data);
    addToast('Promo code added', 'success');
  };

  const handleEditPromoCode = (data: Omit<PromoCode, 'id' | 'offerId' | 'usageCount' | 'createdAt'>) => {
    if (editingPromo) {
      updatePromoCode(offer.id, editingPromo.id, {
        description: data.description,
        effectiveFrom: data.effectiveFrom,
        effectiveUntil: data.effectiveUntil,
        singleUse: data.singleUse,
        maxRedemptions: data.maxRedemptions,
      });
      addToast('Promo code updated', 'success');
    }
  };

  const handleDeactivatePromoCode = (codeId: string) => {
    const activeCodesCount = offer.promoCodes.filter((c) => c.status === 'active').length;
    const hasPromoCodeMode = Object.values(offer.applicationModes).some(
      (mode) => mode === 'promo-code' || mode === 'auto-apply-promo'
    );
    if (activeCodesCount <= 1 && hasPromoCodeMode) {
      addToast('Warning: deactivating the last promo code. Some order type groups are set to promo-code-only mode.', 'warning');
    }
    deactivatePromoCode(offer.id, codeId);
    addToast('Promo code deactivated');
  };

  const handleReactivatePromoCode = (codeId: string) => {
    updatePromoCode(offer.id, codeId, { status: 'active' });
    addToast('Promo code reactivated', 'success');
  };

  const handleDeletePromoCode = (codeId: string) => {
    const code = offer.promoCodes.find((c) => c.id === codeId);
    if (code && code.usageCount > 0) {
      addToast('Cannot delete a promo code that has been used. Deactivate it instead.', 'error');
      return;
    }
    // Remove code by updating the promo codes array
    const updated = offer.promoCodes.filter((c) => c.id !== codeId);
    updateOffer(offer.id, { promoCodes: updated });
    addToast('Promo code deleted');
  };

  // Location override handlers
  const handleAddOverride = (override: OfferLocationOverride) => {
    addLocationOverride(offer.id, override);
    addToast('Location override added', 'success');
  };

  const handleEditOverride = (override: OfferLocationOverride) => {
    updateLocationOverride(offer.id, override.locationId, override);
    addToast('Location override updated', 'success');
  };

  const handleResetOverride = (locationId: string) => {
    removeLocationOverride(offer.id, locationId);
    addToast('Location override removed — using brand defaults', 'success');
  };

  const metrics = offerMetrics[offer.id];
  const showPerformance = offer.status !== 'draft';

  const tabs = [
    { key: 'overview', label: 'Overview' },
    // ...(showPerformance ? [{ key: 'performance', label: 'Performance' }] : []),
  ];

  return (
    <DesktopLayout>
    <div className="offer-detail-page">
      <Breadcrumb items={[{ label: 'Offers', to: '/offers' }, { label: offer.name }]} />
      <PageHeader
        icon={<IconTag size={24} color="rgba(255,255,255,0.95)" />}
        title={offer.name}
        subtitle={offer.description || 'No description.'}
        actions={
          <div className="offer-detail-header-actions">
            {offer.status !== 'expired' && (
              <Button variant="ghost" onClick={() => navigate(`/offers/${offer.id}/edit`)}>Edit</Button>
            )}
            <Button variant="ghost" onClick={handleDuplicate}>Duplicate</Button>
            {offer.status === 'draft' && (
              <>
                <Button variant="primary" onClick={() => handleTransition('active')}>Activate</Button>
                <Button variant="ghost" className="offer-detail-btn-red" onClick={() => setConfirmAction({ type: 'delete' })}>Delete</Button>
              </>
            )}
            {offer.status === 'active' && (
              <Button variant="secondary" className="offer-detail-btn-orange" onClick={() => setConfirmAction({ type: 'pause', status: 'paused' })}>Pause</Button>
            )}
            {offer.status === 'paused' && (
              <>
                <Button variant="primary" onClick={() => handleTransition('active')}>Resume</Button>
                <Button variant="ghost" onClick={() => handleTransition('archived')}>Archive</Button>
              </>
            )}
            {offer.status === 'expired' && (
              <Button variant="ghost" onClick={() => handleTransition('archived')}>Archive</Button>
            )}
            {offer.status === 'archived' && (
              <Button variant="ghost" onClick={() => handleTransition('draft')}>Restore to Draft</Button>
            )}
          </div>
        }
      />

      {/* Status bar */}
      <div className="offer-detail-status-bar">
        <StatusBadge status={offer.status} />
        {offer.isExclusive && <Badge variant="warning">Exclusive</Badge>}
        <span className="offer-detail-meta">
          Created {formatDate(offer.createdAt)} · Updated {formatDate(offer.updatedAt)} · v{offer.version}
        </span>
      </div>

      <Tabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

      {activeTab === 'overview' && (
        <Card>
          <h3 className="offer-detail-section-title">Basic Information</h3>
          <InfoRow label="Type" value={typeLabels[offer.type]} />
          {offer.type !== 'bogo' && offer.type !== 'free-item' && offer.type !== 'free-delivery' && (
            <InfoRow label="Discount Value" value={offer.type === 'flat-dollar-off' ? formatCurrency(offer.discountValue) : offer.type === 'combo-bundle' ? 'See type config' : `${offer.discountValue}%`} />
          )}
          <InfoRow label="Application Level" value={offer.applicationLevel === 'order' ? 'Order Level' : 'Item Level'} />
          <InfoRow label="Order Types" value={offer.orderTypes.map((ot) => {
            const labels: Record<string, string> = { 'dine-in': 'Dine-In', 'walk-in': 'Walk-In', 'phone': 'Phone', 'table-kiosk': 'Table Kiosk', 'online-pickup': 'Online Pickup', 'online-delivery': 'Online Delivery', 'station-kiosk': 'Station Kiosk' };
            return orderTypeLabelById[ot] || labels[ot] || ot;
          }).join(', ')} />
          <InfoRow label="Location Scope" value={offer.locationScope === 'all' ? 'All Locations' : offer.locationIds.map(getLocationName).join(', ')} />
          <InfoRow label="Total Redemptions" value={!offer.eligibility.maxRedemptionsTotal ? '0 (Unlimited)' : formatNumber(offer.eligibility.maxRedemptionsTotal)} />
          {offer.internalNotes && <InfoRow label="Internal Notes" value={offer.internalNotes} />}

          {/* Type-specific details */}
          {offer.typeConfig && (
            <>
              <h3 className="offer-detail-section-title with-top">Type-Specific Configuration</h3>
              {offer.typeConfig.kind === 'bogo' && (
                <>
                  <InfoRow label="Buy Items" value={offer.typeConfig.buyItemIds.map(getItemName).join(', ')} />
                  <InfoRow label="Buy Quantity" value={offer.typeConfig.buyQuantity} />
                  <InfoRow label="Get Items" value={offer.typeConfig.getItemIds.map(getItemName).join(', ')} />
                  <InfoRow label="Get Discount" value={`${offer.typeConfig.getDiscountPercent}%${offer.typeConfig.getDiscountPercent === 100 ? ' (Free)' : ''}`} />
                  <InfoRow label="Equal or Lesser Value" value={offer.typeConfig.equalOrLesserValue ? 'Yes' : 'No'} />
                  {offer.typeConfig.excludeItemIds?.length > 0 && (
                    <InfoRow label="Excluded Items" value={offer.typeConfig.excludeItemIds.map(getItemName).join(', ')} />
                  )}
                </>
              )}
              {offer.typeConfig.kind === 'free-item' && (
                <>
                  <InfoRow label="Min Spend" value={formatCurrency(offer.typeConfig.minSpend)} />
                  <InfoRow label="Free Items" value={offer.typeConfig.freeItemIds.map(getItemName).join(', ')} />
                  <InfoRow label="Max Free per Order" value={offer.typeConfig.maxFreeItems} />
                </>
              )}
              {offer.typeConfig.kind === 'combo-bundle' && (
                <>
                  <InfoRow label="Bundle Name" value={offer.typeConfig.bundleName || '—'} />
                  <InfoRow label="Bundle Price" value={formatCurrency(offer.typeConfig.bundlePrice)} />
                  <InfoRow label="Bundle Items" value={offer.typeConfig.bundleItemIds.map(getItemName).join(', ')} />
                </>
              )}
              {offer.typeConfig.kind === 'happy-hour' && (
                <>
                  <InfoRow label="Days" value={offer.typeConfig.days.map((d) => dayNames[d]).join(', ')} />
                  <InfoRow label="Time Window" value={`${offer.typeConfig.startTime} – ${offer.typeConfig.endTime}`} />
                </>
              )}
              {offer.typeConfig.kind === 'first-time' && (
                <InfoRow label="Require Profile" value={offer.typeConfig.requireProfile ? 'Yes' : 'No'} />
              )}
              {offer.typeConfig.kind === 'loyalty-linked' && (
                <>
                  <InfoRow label="Tier" value={offer.typeConfig.tierId ? getTierName(offer.typeConfig.tierId) : 'Any Member'} />
                  <InfoRow label="Points Cost" value={offer.typeConfig.pointsCost} />
                  <InfoRow label="Max per Member" value={offer.typeConfig.maxRedemptionsPerMember ?? 'Unlimited'} />
                </>
              )}
            </>
          )}

          {/* Schedule */}
          <h3 className="offer-detail-section-title with-top">Schedule</h3>
          <InfoRow label="Date Range" value={`${formatDate(offer.schedule.startDate)}${offer.schedule.noEndDate ? ' — No end date' : offer.schedule.endDate ? ` — ${formatDate(offer.schedule.endDate)}` : ''}`} />
          {offer.schedule.recurring && (
            <InfoRow label="Recurring" value={offer.schedule.recurring.allDay ? `${offer.schedule.recurring.days.map((d) => dayNames[d]).join(', ')} (All Day)` : `${offer.schedule.recurring.days.map((d) => dayNames[d]).join(', ')}, ${offer.schedule.recurring.startTime} – ${offer.schedule.recurring.endTime}`} />
          )}

          {/* Eligibility */}
          <h3 className="offer-detail-section-title with-top">Eligibility</h3>
          {offer.type !== 'bogo' && (
            <InfoRow label="Min Check" value={offer.eligibility.minCheckSubtotal ? formatCurrency(offer.eligibility.minCheckSubtotal) : 'No minimum'} />
          )}
          <InfoRow label="Max Check" value={offer.eligibility.maxCheckSubtotal ? formatCurrency(offer.eligibility.maxCheckSubtotal) : 'No maximum'} />
          <InfoRow label="Required Items" value={offer.eligibility.requiredItemIds.length > 0 ? offer.eligibility.requiredItemIds.map(getItemName).join(', ') : 'Any'} />
<InfoRow label="Excluded Items" value={offer.eligibility.excludedItemIds.length > 0 ? offer.eligibility.excludedItemIds.map(getItemName).join(', ') : 'None'} />
          <InfoRow label="Max Total Uses" value={offer.eligibility.maxRedemptionsTotal ?? 'Unlimited'} />
          <InfoRow label="Max Per Customer" value={offer.eligibility.maxRedemptionsPerCustomer ?? 'Unlimited'} />

          {/* Application Modes */}
          <h3 className="offer-detail-section-title with-top">Application Modes</h3>
          <InfoRow label="In-Store" value={offer.applicationModes.inStore} />
          <InfoRow label="Digital" value={offer.applicationModes.digital} />
        </Card>
      )}

      {/* ═══ Performance Tab ═══ */}
      {activeTab === 'performance' && metrics && (
        <div className="offer-detail-performance">
          {/* Mini KPIs */}
          <KPIRow>
            <KPICard label="Redemptions" value={formatNumber(metrics.totalRedemptions)} icon={<IconActivity size={18} />} />
            <KPICard label="Discount Given" value={formatCurrency(metrics.totalDiscountGiven)} icon={<IconDollar size={18} />} />
            <KPICard
              label="Avg Order Value"
              value={formatCurrency(metrics.avgOrderValue)}
              delta={Math.round(((metrics.avgOrderValue - metrics.avgOrderValueWithout) / metrics.avgOrderValueWithout) * 100 * 10) / 10}
              trend={metrics.avgOrderValue >= metrics.avgOrderValueWithout ? 'up' : 'down'}
            />
          </KPIRow>

          {/* Redemptions by Day Chart */}
          <Card>
            <div className="offer-detail-chart-heading-sm">
              <span className="offer-detail-chart-title">Redemptions (Last 30 Days)</span>
            </div>
            <LineChartComponent
              data={metrics.redemptionsByDay}
              xKey="date"
              lines={[{ key: 'count', color: 'var(--accent)', name: 'Redemptions' }]}
              height={220}
              showGrid
              showArea
              formatX={(v: string) => formatDateShort(v)}
            />
          </Card>

          {/* Two columns: Top Items + Order Type Breakdown */}
          <div className="offer-detail-grid-two">
            {/* Top Items */}
            <Card>
              <div className="offer-detail-chart-heading">
                <span className="offer-detail-chart-title">Top Items Ordered</span>
              </div>
              {metrics.topItems.map((item, idx) => (
                <div key={item.name} className={`offer-detail-top-item-row ${idx < metrics.topItems.length - 1 ? '' : 'last'}`}>
                  <span className="offer-detail-top-item-name">{idx + 1}. {item.name}</span>
                  <span className="offer-detail-top-item-count">{item.count}</span>
                </div>
              ))}
            </Card>

            {/* Order Type Breakdown */}
            <Card>
              <div className="offer-detail-chart-heading">
                <span className="offer-detail-chart-title">Redemptions by Order Type</span>
              </div>
              <BarChartComponent
                data={metrics.redemptionsByOrderType}
                xKey="type"
                bars={[{ key: 'count', color: 'var(--accent)', name: 'Redemptions' }]}
                height={180}
                layout="horizontal"
              />
            </Card>
          </div>
        </div>
      )}

      {/* ═══ Confirm Dialogs ═══ */}
      <ConfirmDialog
        open={confirmAction?.type === 'pause'}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction?.status) void handleTransition(confirmAction.status);
          setConfirmAction(null);
        }}
        title="Pause Offer"
        message={`This offer has been redeemed ${offer.redemptionCount.toLocaleString()} times. Pausing will stop all future redemptions immediately. Continue?`}
        confirmText="Pause"
        variant="warning"
      />

      <ConfirmDialog
        open={confirmAction?.type === 'delete'}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => { handleDelete(); setConfirmAction(null); }}
        title={offer.redemptionCount > 0 ? 'Archive Offer' : 'Delete Draft Offer'}
        message={offer.redemptionCount > 0
          ? 'This offer has redemption history and will be archived instead of deleted.'
          : 'Permanently delete this draft offer? This cannot be undone.'}
        confirmText={offer.redemptionCount > 0 ? 'Archive' : 'Delete'}
        variant="danger"
      />

    </div>
    </DesktopLayout>
  );
}
